import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLoading } from '../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

const ScrollStorySection = ({ stories, className = '' }) => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const { isInitialized } = useLoading();

  useEffect(() => {
    if (!isInitialized) return;

    const section = sectionRef.current;
    const content = contentRef.current;

    // Set up the scroll-triggered story progression
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top ceneter+=600',
        end: `+=${window.innerHeight * (stories.length - 0.5) * 0.5}`,
        anticipatePin: 1,
        scrub: 1,
        invalidateOnRefresh: true,
        markers: false
      }
    });

    // Create animations for each story
    stories.forEach((story, index) => {
      const isFirst = index === 0;
      const isLast = index === stories.length - 1;

      // Fade in
      tl.fromTo(
        `[data-story="${index}"]`,
        {
          opacity: 0,
          scale: 0.8
        },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          immediateRender: isFirst
        },
        index
      );

      // Remove the static hold; advance straight to the fade-out
      tl.to(
        `[data-story="${index}"]`,
        {
          opacity: 1,
          scale: 1,
          duration: 0
        },
        '>'
      );

      // Fade out (except for the last story)
        tl.to(
          `[data-story="${index}"]`,
          {
            opacity: 0,
            scale: 1.5,
            duration: 1
          },
          '>'
        );
    });

    // After setting up per-story timeline, animate the entire content block vertically
    gsap.fromTo(
      content,
      { y: 0 },
      {
        y: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top center',
          end: 'bottom+=200 center',
          scrub: 1,
          invalidateOnRefresh: true
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [stories, isInitialized]);

  return (
    <section
      ref={sectionRef}
      className={`relative h-[50vh] ${className}`}
    >
      <div
        ref={contentRef}
        className="absolute inset-0 flex items-center justify-center"
      >
        {stories.map((story, index) => (
          <div
            key={index}
            data-story={index}
            className="absolute w-full max-w-4xl mx-auto px-4 opacity-0"
          >
            <div className="bg-white rounded-2xl shadow-xl p-8 transform-gpu">
              {story.icon && (
                <div className="mb-6">
                  <story.icon className="h-12 w-12 text-blue-600" />
                </div>
              )}
              <h3 className="text-3xl font-bold mb-4">{story.title}</h3>
              <p className="text-xl text-gray-600">{story.description}</p>
              {story.content}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ScrollStorySection; 